import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (token) {
      pollPaymentStatus();
    }
  }, [token]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.subscription_status === "active") {
        setStatus("success");
        return;
      } else if (response.data.status === "incomplete_expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(attempts + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-[#0a1a1f]/80 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 p-8 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-[#00ced1] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-[#f8f8f8] mb-2">Processing Payment...</h2>
            <p className="text-[#f8f8f8]/70">Please wait while we confirm your subscription</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#00ced1] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#f8f8f8] mb-2">Subscription Activated!</h2>
            <p className="text-[#f8f8f8]/70 mb-6">
              Thank you for subscribing. Your account is now active.
            </p>
            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold w-full py-6 rounded-xl"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <h2 className="text-2xl font-bold text-[#f8f8f8] mb-2">Payment Error</h2>
            <p className="text-[#f8f8f8]/70 mb-6">
              There was an error processing your payment. Please try again.
            </p>
            <Button
              onClick={() => navigate("/subscription-required")}
              className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold w-full py-6 rounded-xl"
            >
              Try Again
            </Button>
          </>
        )}

        {status === "timeout" && (
          <>
            <h2 className="text-2xl font-bold text-[#f8f8f8] mb-2">Verification Taking Longer</h2>
            <p className="text-[#f8f8f8]/70 mb-6">
              Payment verification is taking longer than expected. Check your email for confirmation.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold w-full py-6 rounded-xl"
            >
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
